import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axiosInstance from '../../lib/axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
  BarChart3, Users, Coins, FlaskConical, Download, Loader2, Plus,
  ChevronDown, ChevronRight, RefreshCw, Trash2, Search, Eye, Pencil, Power, CalendarClock,
} from 'lucide-react';
import {
  formatMXN, formatUSD, formatInt, byDayToCSV, byUserToCSV, downloadCSV,
} from '../lib/dashboardFormat';
import { CREDITS_UPDATED_EVENT } from '../lib/credits';
// ModelsPricingCard vive en AdminSettings; el /admin dashboard lo incluye una vez.
import CreditCalculator from './CreditCalculator';
import ContentStatsCard from './ContentStatsCard';

/** Id del usuario logueado (para refrescar SU badge tras un grant a sí mismo). */
function currentUserId() {
  try { return JSON.parse(localStorage.getItem('user') || '{}').id || null; }
  catch { return null; }
}

const SELECT_CLS =
  'h-9 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-3 text-sm';
const OPT_CLS = 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100';
const USER_PAGE = 25; // users per page in the admin user list

const KPI = ({ label, value, sub, accent }) => (
  <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-white/60 dark:bg-slate-800/40">
    <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
    <div className={`text-xl font-bold mt-1 ${accent || ''}`}>{value}</div>
    {sub && <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{sub}</div>}
  </div>
);

// Collapsible card: keeps the existing header visible with a chevron toggle so
// admins can fold sections they aren't using ("para no ocupar tanto espacio").
// The FIRST child is treated as the header (always shown); the rest is the body
// (hidden when collapsed) — so wrapping a card is just swapping div→CollapsibleCard.
const CollapsibleCard = ({ spaceClass = 'space-y-3', children }) => {
  const [open, setOpen] = useState(true);
  const kids = React.Children.toArray(children);
  const [head, ...body] = kids;
  return (
    <div className={`glass rounded-xl p-6 ${spaceClass}`}>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="shrink-0 text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
          aria-label={open ? 'Colapsar' : 'Expandir'}
        >
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="flex-1 min-w-0">{head}</div>
      </div>
      {open && body}
    </div>
  );
};

const AdminDashboard = ({ section = null } = {}) => {
  // section: 'stats' | 'users' | 'credits' | null. null = renderiza todo
  // (compat). El /admin usa el menú lateral para pedir un solo bloque.
  const show = (s) => !section || section === s;
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [byDayMetric, setByDayMetric] = useState('credits_consumed');

  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({ credit_value_mxn: '', fx_usd_mxn: '', credit_markup: '', require_subscription: false, daily_trial_credits: '', credit_packages: [] });

  // user filters
  const [userSearch, setUserSearch] = useState('');
  const [userRole, setUserRole] = useState('');   // '' = todos
  const [userActive, setUserActive] = useState(''); // '' = todos | 'true' | 'false'

  // user pagination (backend /admin/users returns { total, limit, offset, users })
  const [userOffset, setUserOffset] = useState(0);
  const [userTotal, setUserTotal] = useState(0);

  // user detail modal
  const [detailUser, setDetailUser] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);

  // user edit modal
  const [editUser, setEditUser] = useState(null);
  const [editRole, setEditRole] = useState('user');
  const [editActive, setEditActive] = useState(true);
  const [editBusy, setEditBusy] = useState(false);

  // credit grant modal
  const [creditUser, setCreditUser] = useState(null);
  const [creditPkg, setCreditPkg] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [creditNote, setCreditNote] = useState('');
  const [grantBusy, setGrantBusy] = useState(false);

  // subscription (plan mensual) modal
  const [subUser, setSubUser] = useState(null);
  const [subMonths, setSubMonths] = useState(1);
  const [subBusy, setSubBusy] = useState(false);

  // credit config
  const [savingCfg, setSavingCfg] = useState(false);

  // dev panel
  const [devOpen, setDevOpen] = useState(false);
  const [simDays, setSimDays] = useState(30);
  const [simEvents, setSimEvents] = useState(20);
  const [simSeed, setSimSeed] = useState('');
  const [devBusy, setDevBusy] = useState(false);

  const loadStats = useCallback(async (d) => {
    setLoadingStats(true);
    try {
      const res = await axiosInstance.get('/admin/stats', { params: { days: d } });
      setStats(res.data);
    } catch (e) {
      if (e.response?.status !== 403) toast.error(e.response?.data?.detail || 'Error al cargar estadísticas');
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const loadUsers = useCallback(async (filters = {}) => {
    const offset = filters.offset || 0;
    try {
      const params = { limit: USER_PAGE, offset };
      if (filters.search) params.search = filters.search;
      if (filters.role) params.role = filters.role;
      if (filters.active === 'true' || filters.active === 'false') params.active = filters.active;
      const res = await axiosInstance.get('/admin/users', { params });
      setUsers(res.data.users || []);
      setUserTotal(res.data.total || 0);
      setUserOffset(offset);
    } catch (e) { /* handled by interceptor */ }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/admin/settings');
      setSettings({
        credit_value_mxn: res.data.credit_value_mxn ?? '',
        fx_usd_mxn: res.data.fx_usd_mxn ?? '',
        credit_markup: res.data.credit_markup ?? '',
        require_subscription: !!res.data.require_subscription,
        daily_trial_credits: res.data.daily_trial_credits ?? '',
        credit_packages: res.data.credit_packages || [],
      });
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { loadStats(days); }, [days, loadStats]);
  useEffect(() => { loadUsers(); loadSettings(); },
    [loadUsers, loadSettings]);

  // Reload the user list with the current filters (search box + selects).
  const refreshUsers = useCallback(
    () => loadUsers({ search: userSearch, role: userRole, active: userActive }),
    [loadUsers, userSearch, userRole, userActive]
  );

  // Jump to a page while keeping the active filters.
  const gotoUsers = useCallback(
    (offset) => loadUsers({ search: userSearch, role: userRole, active: userActive, offset }),
    [loadUsers, userSearch, userRole, userActive]
  );

  const t = stats?.totals || {};
  const tokensTotal = (t.tokens_in || 0) + (t.tokens_out || 0);
  const byDayMax = useMemo(
    () => Math.max(1, ...(stats?.by_day || []).map((d) => Number(d[byDayMetric]) || 0)),
    [stats, byDayMetric]
  );
  // T-088: modo "las 3 juntas" — cada métrica se normaliza contra SU PROPIO
  // máximo (créditos/costo/tokens viven en escalas incomparables).
  const byDayMaxAll = useMemo(() => {
    const days = stats?.by_day || [];
    const mx = (k) => Math.max(1, ...days.map((d) => Number(d[k]) || 0));
    return {
      credits_consumed: mx('credits_consumed'), cost_usd: mx('cost_usd'),
      tokens: mx('tokens'), revenue_mxn: mx('revenue_mxn'),
    };
  }, [stats]);

  // --- credit grant ---
  const openCredit = (u) => {
    setCreditUser(u); setCreditPkg(''); setCreditAmount(''); setCreditNote('');
  };
  const submitCredit = async () => {
    if (!creditPkg && !creditAmount) { toast.error('Elige un paquete o un monto'); return; }
    setGrantBusy(true);
    try {
      const body = { note: creditNote || undefined };
      if (creditPkg) body.package_id = creditPkg;
      else body.amount = Number(creditAmount);
      const res = await axiosInstance.post(`/admin/users/${creditUser.id}/credits`, body);
      setUsers((prev) => prev.map((u) => u.id === creditUser.id ? { ...u, credits_balance: res.data.credits_balance } : u));
      // Si el admin se otorgó créditos a SÍ MISMO, refresca su badge al instante
      // (el badge escucha CREDITS_UPDATED_EVENT). Sin esto seguía en 0 hasta un
      // refresh manual de la página.
      if (creditUser.id === currentUserId()) {
        window.dispatchEvent(new CustomEvent(CREDITS_UPDATED_EVENT, {
          detail: { balance: res.data.credits_balance },
        }));
      }
      toast.success(`Créditos actualizados: ${formatInt(res.data.credits_balance)}`);
      setCreditUser(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al agregar créditos');
    } finally {
      setGrantBusy(false);
    }
  };

  // --- plan mensual (manual) ---
  const openSub = (u) => { setSubUser(u); setSubMonths(1); };
  const submitSub = async (payload) => {
    setSubBusy(true);
    try {
      const res = await axiosInstance.post(`/admin/users/${subUser.id}/subscription`, payload);
      setUsers((prev) => prev.map((u) => u.id === subUser.id ? { ...u, subscription: res.data } : u));
      toast.success(res.data.active
        ? `Plan activo hasta ${new Date(res.data.until).toLocaleDateString('es-MX')}`
        : 'Plan quitado');
      setSubUser(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar el plan');
    } finally {
      setSubBusy(false);
    }
  };

  // --- user detail / edit / activation ---
  const openDetail = async (u) => {
    setDetailUser(u); setDetailData(null); setDetailBusy(true);
    try {
      const res = await axiosInstance.get(`/admin/users/${u.id}`);
      setDetailData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar el detalle');
      setDetailUser(null);
    } finally {
      setDetailBusy(false);
    }
  };

  const openEdit = (u) => {
    setEditUser(u); setEditRole(u.role || 'user'); setEditActive(u.active !== false);
  };

  const submitEdit = async () => {
    setEditBusy(true);
    try {
      await axiosInstance.patch(`/admin/users/${editUser.id}`, { role: editRole, active: editActive });
      toast.success('Usuario actualizado');
      setEditUser(null);
      refreshUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar el usuario');
    } finally {
      setEditBusy(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await axiosInstance.patch(`/admin/users/${u.id}`, { active: u.active === false });
      toast.success(u.active === false ? 'Cuenta activada' : 'Cuenta desactivada');
      refreshUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cambiar el estado');
    }
  };

  // --- credit config ---
  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      await axiosInstance.put('/admin/settings', {
        credit_value_mxn: Number(settings.credit_value_mxn) || 0,
        fx_usd_mxn: Number(settings.fx_usd_mxn) || 0,
        credit_markup: Number(settings.credit_markup) || 0,
        require_subscription: !!settings.require_subscription,
        daily_trial_credits: Number(settings.daily_trial_credits) || 0,
        credit_packages: settings.credit_packages,
      });
      toast.success('Configuración de créditos guardada');
      loadSettings();
      loadStats(days);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSavingCfg(false);
    }
  };

  const setPkg = (i, key, val) => setSettings((s) => ({
    ...s,
    credit_packages: s.credit_packages.map((p, idx) => idx === i ? { ...p, [key]: val } : p),
  }));

  // --- dev ---
  const simulate = async () => {
    setDevBusy(true);
    try {
      const body = { days: Number(simDays) || 1, events_per_day: Number(simEvents) || 1 };
      if (simSeed !== '') body.seed = Number(simSeed);
      const res = await axiosInstance.post('/admin/dev/simulate-usage', body);
      toast.success(`Simulados ${formatInt(res.data.created_events)} eventos · ${formatUSD(res.data.total_cost_usd)} · ${formatInt(res.data.total_credits_charged)} créditos`);
      loadStats(days); loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al simular');
    } finally {
      setDevBusy(false);
    }
  };
  const clearSim = async () => {
    setDevBusy(true);
    try {
      const res = await axiosInstance.delete('/admin/dev/simulate-usage');
      toast.success(`Limpiados ${formatInt(res.data.deleted_events)} eventos y ${formatInt(res.data.deleted_ledger)} movimientos`);
      loadStats(days); loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al limpiar');
    } finally {
      setDevBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {show('stats') && (<>
      {/* Header + range */}
      <CollapsibleCard spaceClass="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <label className="text-sm font-medium flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-purple-600" />
            Estadísticas y créditos
          </label>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 dark:text-slate-400">Rango</span>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} className={SELECT_CLS} data-testid="dash-range">
              <option value={7} className={OPT_CLS}>7 días</option>
              <option value={30} className={OPT_CLS}>30 días</option>
              <option value={90} className={OPT_CLS}>90 días</option>
            </select>
            <Button variant="secondary" size="sm" onClick={() => loadStats(days)} disabled={loadingStats}>
              {loadingStats ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <KPI label="Ingreso (solo ventas)" value={formatMXN(t.revenue_mxn)} accent="text-emerald-600 dark:text-emerald-400" sub="Paquetes vendidos (pre-IVA)" />
          <KPI label="Costo OpenRouter" value={formatMXN(t.cost_mxn)} sub={formatUSD(t.cost_usd)} />
          <KPI label="Utilidad libre" value={formatMXN(t.profit_mxn)} accent="text-purple-600 dark:text-purple-400" sub="Ingreso − costo IA − infra" />
          <KPI label="Créditos consumidos" value={formatInt(t.credits_consumed)} sub={`Otorgados: ${formatInt(t.credits_granted)}`} />
          <KPI label="Tokens (in+out)" value={formatInt(tokensTotal)} sub={`In ${formatInt(t.tokens_in)} · Out ${formatInt(t.tokens_out)}`} />
        </div>

        {/* T-088: desglose del destino del ingreso — infra, impuestos y utilidad
            libre por separado (petición del dueño). El IVA es TRASLADADO (los
            paquetes se cotizan pre-IVA): lo paga el cliente encima del precio y
            se entera al SAT — no sale de la utilidad. */}
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2" data-testid="dash-profit-breakdown">
          <span className="text-sm font-medium">Desglose del ingreso ({formatMXN(t.revenue_mxn)})</span>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div className="flex items-center justify-between rounded-md bg-indigo-50 dark:bg-indigo-900/30 px-3 py-2">
              <span className="text-indigo-700 dark:text-indigo-300">Reserva infraestructura (20%)</span>
              <strong className="tabular-nums">{formatMXN(t.infra_mxn)}</strong>
            </div>
            <div className="flex items-center justify-between rounded-md bg-amber-50 dark:bg-amber-900/30 px-3 py-2">
              <span className="text-amber-700 dark:text-amber-300">Impuestos — IVA 16% (trasladado)</span>
              <strong className="tabular-nums">{formatMXN(t.iva_mxn)}</strong>
            </div>
            <div className="flex items-center justify-between rounded-md bg-purple-50 dark:bg-purple-900/30 px-3 py-2">
              <span className="text-purple-700 dark:text-purple-300">Utilidad libre</span>
              <strong className="tabular-nums">{formatMXN(t.profit_mxn)}</strong>
            </div>
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Utilidad libre = ingreso − costo IA ({formatMXN(t.cost_mxn)}) − reserva de infraestructura.
            El IVA se cobra ENCIMA del precio pre-IVA (el cliente lo paga; tú lo enteras al SAT), por eso no resta utilidad.
          </p>
        </div>
        <div className="flex flex-wrap gap-4 text-xs text-slate-500 dark:text-slate-400">
          <span>Cortesías (comped): <strong className="text-amber-600 dark:text-amber-400">{formatMXN(t.comped_mxn)}</strong> · {formatInt(t.comped_credits)} créditos — no cuentan como ingreso</span>
          <span>1 crédito = {formatMXN(stats?.credit_value_mxn)} · FX {formatMXN(stats?.fx_usd_mxn)}/USD</span>
        </div>
      </CollapsibleCard>

      {/* by_day chart (CSS bars) */}
      <CollapsibleCard spaceClass="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="text-sm font-medium">Por día</span>
          <div className="flex items-center gap-2">
            <select value={byDayMetric} onChange={(e) => setByDayMetric(e.target.value)} className={SELECT_CLS} data-testid="dash-byday-metric">
              <option value="credits_consumed" className={OPT_CLS}>Créditos</option>
              <option value="cost_usd" className={OPT_CLS}>Costo USD</option>
              <option value="tokens" className={OPT_CLS}>Tokens</option>
              <option value="revenue_mxn" className={OPT_CLS}>Ingresos MXN</option>
              <option value="all" className={OPT_CLS}>Todas juntas</option>
            </select>
            <Button variant="outline" size="sm" onClick={() => downloadCSV(`stats_por_dia_${days}d.csv`, byDayToCSV(stats?.by_day))}>
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
          </div>
        </div>
        {(stats?.by_day || []).length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">Sin datos en el rango.</p>
        ) : byDayMetric === 'all' ? (
          <>
            {/* T-088: las 3 métricas por día, lado a lado; cada una normalizada
                a su propio máximo (escalas incomparables). */}
            <div className="flex items-center gap-4 text-[11px] text-slate-600 dark:text-slate-300 flex-wrap">
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-gradient-to-t from-purple-500 to-indigo-400" /> Créditos</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-gradient-to-t from-emerald-500 to-teal-400" /> Costo USD</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-gradient-to-t from-sky-500 to-cyan-400" /> Tokens</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-gradient-to-t from-rose-500 to-pink-400" /> Ingresos MXN</span>
            </div>
            <div className="flex items-end gap-2 h-48 overflow-x-auto pb-1">
              {stats.by_day.map((d) => {
                const cr = Number(d.credits_consumed) || 0;
                const cu = Number(d.cost_usd) || 0;
                const tk = Number(d.tokens) || 0;
                const rv = Number(d.revenue_mxn) || 0;
                const tip = `${d.date} · Créditos: ${Math.round(cr * 100) / 100} · Costo: $${cu.toFixed(4)} · Tokens: ${formatInt(tk)} · Ingresos: ${formatMXN(rv)}`;
                const bars = [
                  { v: cr, max: byDayMaxAll.credits_consumed, cls: 'from-purple-500 to-indigo-400' },
                  { v: cu, max: byDayMaxAll.cost_usd, cls: 'from-emerald-500 to-teal-400' },
                  { v: tk, max: byDayMaxAll.tokens, cls: 'from-sky-500 to-cyan-400' },
                  { v: rv, max: byDayMaxAll.revenue_mxn, cls: 'from-rose-500 to-pink-400' },
                ];
                return (
                  <div key={d.date} className="flex-1 min-w-[68px] flex flex-col items-center justify-end h-full" title={tip}>
                    <div className="w-full flex items-end justify-center gap-0.5 flex-1">
                      {bars.map((b, i) => (
                        <div
                          key={i}
                          className={`w-1/4 max-w-[14px] rounded-t bg-gradient-to-t ${b.cls}`}
                          style={{ height: `${Math.max(Math.round((b.v / b.max) * 100), 2)}%` }}
                        />
                      ))}
                    </div>
                    <span className="text-[9px] text-slate-500 dark:text-slate-400 whitespace-nowrap">{d.date.slice(5)}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-400">Cada métrica se escala a su propio máximo; los valores exactos aparecen al pasar el cursor.</p>
          </>
        ) : (
          <div className="flex items-end gap-1 h-48 overflow-x-auto pb-1">
            {stats.by_day.map((d) => {
              const v = Number(d[byDayMetric]) || 0;
              const h = Math.round((v / byDayMax) * 100);
              // T-087: valor visible sobre la barra y fecha bajo ella (antes solo
              // tooltip al hover — el dueño veía "dos barras de color" sin datos).
              const label = byDayMetric === 'cost_usd'
                ? `$${v.toFixed(v < 0.01 ? 4 : 2)}`
                : byDayMetric === 'revenue_mxn'
                  ? formatMXN(v)
                  : byDayMetric === 'tokens'
                    ? formatInt(v)
                    : String(Math.round(v * 100) / 100);
              return (
                <div key={d.date} className="flex-1 min-w-[42px] flex flex-col items-center justify-end h-full" title={`${d.date}: ${v}`}>
                  <span className="text-[10px] text-slate-600 dark:text-slate-300 tabular-nums whitespace-nowrap">{label}</span>
                  <div className="w-full rounded-t bg-gradient-to-t from-purple-500 to-indigo-400" style={{ height: `${Math.max(h, 2)}%` }} />
                  <span className="text-[9px] text-slate-500 dark:text-slate-400 whitespace-nowrap">{d.date.slice(5)}</span>
                </div>
              );
            })}
          </div>
        )}
      </CollapsibleCard>

      {/* by_model table */}
      <CollapsibleCard spaceClass="space-y-3">
        <span className="text-sm font-medium">Por modelo</span>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <th className="py-2 pr-2">Modelo</th><th className="py-2 px-2 text-right">Llamadas</th>
                <th className="py-2 px-2 text-right">Tokens</th><th className="py-2 pl-2 text-right">Costo USD</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.by_model || []).map((m) => (
                <tr key={m.model} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="py-2 pr-2 font-mono text-xs">{m.model}</td>
                  <td className="py-2 px-2 text-right">{formatInt(m.calls)}</td>
                  <td className="py-2 px-2 text-right">{formatInt(m.tokens)}</td>
                  <td className="py-2 pl-2 text-right">{formatUSD(m.cost_usd)}</td>
                </tr>
              ))}
              {(stats?.by_model || []).length === 0 && (
                <tr><td colSpan={4} className="py-3 text-slate-500 dark:text-slate-400">Sin datos.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CollapsibleCard>

      {/* by_user top table */}
      <CollapsibleCard spaceClass="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="text-sm font-medium">Top usuarios por consumo</span>
          <Button variant="outline" size="sm" onClick={() => downloadCSV(`stats_por_usuario_${days}d.csv`, byUserToCSV(stats?.by_user))}>
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <th className="py-2 pr-2">Usuario</th><th className="py-2 px-2 text-right">Créditos</th><th className="py-2 pl-2 text-right">Costo USD</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.by_user || []).map((u) => (
                <tr key={u.user_id} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="py-2 pr-2">{u.email}</td>
                  <td className="py-2 px-2 text-right">{formatInt(u.credits_consumed)}</td>
                  <td className="py-2 pl-2 text-right">{formatUSD(u.cost_usd)}</td>
                </tr>
              ))}
              {(stats?.by_user || []).length === 0 && (
                <tr><td colSpan={3} className="py-3 text-slate-500 dark:text-slate-400">Sin datos.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CollapsibleCard>

      {/* T-088: desglose de cortesías (manual_grant) y ajustes (adjust). */}
      <CollapsibleCard spaceClass="space-y-3">
        <span className="text-sm font-medium">🎁 Cortesías y ajustes (ventana seleccionada)</span>
        <div className="flex flex-wrap gap-4 text-xs text-slate-600 dark:text-slate-300">
          <span>Total cortesías: <strong className="text-amber-600 dark:text-amber-400">{formatInt(t.comped_credits)} créditos</strong> = {formatMXN(t.comped_mxn)} (valor no facturado; no es ingreso ni causa IVA)</span>
        </div>

        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Por usuario</span>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="dash-comped-users">
            <thead>
              <tr className="text-left text-xs text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <th className="py-2 pr-2">Usuario</th>
                <th className="py-2 px-2 text-right"># Cortesías</th>
                <th className="py-2 px-2 text-right">Créditos regalados</th>
                <th className="py-2 px-2 text-right">Valor MXN</th>
                <th className="py-2 pl-2 text-right">Ajustes (retirados)</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.comped?.by_user || []).map((u) => (
                <tr key={u.user_id} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="py-2 pr-2">{u.email}</td>
                  <td className="py-2 px-2 text-right">{formatInt(u.grants)}</td>
                  <td className="py-2 px-2 text-right">{formatInt(u.granted_credits)}</td>
                  <td className="py-2 px-2 text-right">{formatMXN(u.granted_mxn)}</td>
                  <td className="py-2 pl-2 text-right text-rose-600 dark:text-rose-400">{u.adjusted_credits ? `−${formatInt(u.adjusted_credits)}` : '—'}</td>
                </tr>
              ))}
              {(stats?.comped?.by_user || []).length === 0 && (
                <tr><td colSpan={5} className="py-3 text-slate-500 dark:text-slate-400">Sin cortesías en el rango.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Movimientos recientes</span>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="dash-comped-recent">
            <thead>
              <tr className="text-left text-xs text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <th className="py-2 pr-2">Fecha</th><th className="py-2 px-2">Usuario</th>
                <th className="py-2 px-2 text-right">Δ Créditos</th>
                <th className="py-2 px-2">Nota</th><th className="py-2 pl-2">Otorgó</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.comped?.recent || []).map((m, i) => (
                <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="py-2 pr-2 font-mono text-xs whitespace-nowrap">{m.date}</td>
                  <td className="py-2 px-2">{m.email}</td>
                  <td className={`py-2 px-2 text-right tabular-nums ${m.delta >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                    {m.delta >= 0 ? `+${m.delta}` : m.delta}
                  </td>
                  <td className="py-2 px-2 text-xs text-slate-500 dark:text-slate-400">{m.note || '—'}</td>
                  <td className="py-2 pl-2 text-xs">{m.admin_email || '—'}</td>
                </tr>
              ))}
              {(stats?.comped?.recent || []).length === 0 && (
                <tr><td colSpan={5} className="py-3 text-slate-500 dark:text-slate-400">Sin movimientos en el rango.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CollapsibleCard>

      {/* T-098 Fase 7: estadísticas de aislamiento/contenido (regla de oro, solo métricas) */}
      <ContentStatsCard />
      </>)}

      {show('users') && (<>
      {/* Users management */}
      <CollapsibleCard spaceClass="space-y-3">
        <span className="text-sm font-medium flex items-center gap-2"><Users className="w-4 h-4 text-purple-600" /> Gestión de usuarios</span>

        {/* filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1">
            <Input
              value={userSearch}
              placeholder="Buscar por email…"
              className="h-9 w-56"
              onChange={(e) => setUserSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') refreshUsers(); }}
              data-testid="user-search"
            />
            <Button variant="secondary" size="sm" onClick={refreshUsers}>
              <Search className="w-4 h-4" />
            </Button>
          </div>
          <select value={userRole} className={SELECT_CLS}
            onChange={(e) => { const v = e.target.value; setUserRole(v); loadUsers({ search: userSearch, role: v, active: userActive }); }}
            data-testid="user-role-filter">
            <option value="" className={OPT_CLS}>Todos los roles</option>
            <option value="user" className={OPT_CLS}>user</option>
            <option value="admin" className={OPT_CLS}>admin</option>
          </select>
          <select value={userActive} className={SELECT_CLS}
            onChange={(e) => { const v = e.target.value; setUserActive(v); loadUsers({ search: userSearch, role: userRole, active: v }); }}
            data-testid="user-active-filter">
            <option value="" className={OPT_CLS}>Todos los estados</option>
            <option value="true" className={OPT_CLS}>Activos</option>
            <option value="false" className={OPT_CLS}>Inactivos</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <th className="py-2 pr-2">Email</th><th className="py-2 px-2">Rol</th>
                <th className="py-2 px-2">Estado</th>
                <th className="py-2 px-2 text-right">Saldo</th><th className="py-2 px-2 text-right">Consumo</th>
                <th className="py-2 px-2 text-right">Costo USD</th><th className="py-2 pl-2"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="py-2 pr-2">{u.email}</td>
                  <td className="py-2 px-2">{u.role}</td>
                  <td className="py-2 px-2">
                    {u.active === false
                      ? <span className="text-red-600 dark:text-red-400">Inactivo</span>
                      : <span className="text-emerald-600 dark:text-emerald-400">Activo</span>}
                  </td>
                  <td className="py-2 px-2 text-right">{formatInt(u.credits_balance)}</td>
                  <td className="py-2 px-2 text-right">{formatInt(u.total_credits_consumed)}</td>
                  <td className="py-2 px-2 text-right">{formatUSD(u.total_cost_usd)}</td>
                  <td className="py-2 pl-2 text-right">
                    <div className="flex justify-end gap-1 flex-wrap">
                      <Button variant="outline" size="sm" onClick={() => openDetail(u)} data-testid="user-view">
                        <Eye className="w-4 h-4 mr-1" /> Ver
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openEdit(u)} data-testid="user-edit">
                        <Pencil className="w-4 h-4 mr-1" /> Editar
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => toggleActive(u)}
                        className={u.active === false ? 'text-emerald-600' : 'text-red-600'} data-testid="user-toggle">
                        <Power className="w-4 h-4 mr-1" /> {u.active === false ? 'Activar' : 'Desactivar'}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openCredit(u)}>
                        <Plus className="w-4 h-4 mr-1" /> Créditos
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openSub(u)} data-testid="user-sub"
                        className={u.subscription?.active ? 'text-emerald-600 dark:text-emerald-400' : ''}
                        title={u.subscription?.active ? `Plan activo hasta ${new Date(u.subscription.until).toLocaleDateString('es-MX')}` : 'Sin plan'}>
                        <CalendarClock className="w-4 h-4 mr-1" /> {u.subscription?.active ? 'Plan ✓' : 'Plan'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={7} className="py-3 text-slate-500 dark:text-slate-400">Sin usuarios.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {userTotal > 0 && (
          <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
            <Button variant="outline" size="sm" disabled={userOffset === 0}
              onClick={() => gotoUsers(Math.max(0, userOffset - USER_PAGE))}>
              Anterior
            </Button>
            <span className="text-xs text-slate-600 dark:text-slate-400">
              Mostrando {userOffset + 1}-{Math.min(userOffset + USER_PAGE, userTotal)} de {userTotal}
            </span>
            <Button variant="outline" size="sm" disabled={userOffset + USER_PAGE >= userTotal}
              onClick={() => gotoUsers(userOffset + USER_PAGE)}>
              Siguiente
            </Button>
          </div>
        )}
      </CollapsibleCard>
      </>)}

      {show('credits') && (<>
      {/* Credit config */}
      <CollapsibleCard spaceClass="space-y-4">
        <span className="text-sm font-medium flex items-center gap-2"><Coins className="w-4 h-4 text-purple-600" /> Configuración de créditos</span>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label>Valor del crédito (MXN)</Label>
            <Input type="number" step="0.01" value={settings.credit_value_mxn}
              onChange={(e) => setSettings((s) => ({ ...s, credit_value_mxn: e.target.value }))} data-testid="cfg-credit-value" />
          </div>
          <div className="space-y-1">
            <Label>Margen (factor)</Label>
            <Input type="number" step="0.05" value={settings.credit_markup}
              onChange={(e) => setSettings((s) => ({ ...s, credit_markup: e.target.value }))} data-testid="cfg-markup" />
          </div>
          <div className="space-y-1">
            <Label>Tipo de cambio USD→MXN</Label>
            <Input type="number" step="0.01" value={settings.fx_usd_mxn}
              onChange={(e) => setSettings((s) => ({ ...s, fx_usd_mxn: e.target.value }))} data-testid="cfg-fx" />
          </div>
          <div className="space-y-1">
            <Label>Créditos diarios de prueba</Label>
            <Input type="number" step="0.01" value={settings.daily_trial_credits}
              onChange={(e) => setSettings((s) => ({ ...s, daily_trial_credits: e.target.value }))} data-testid="daily-trial-input" />
            <p className="text-xs text-slate-500 dark:text-slate-400">Cada usuario se rellena hasta este saldo una vez al día (no acumulable). 0 lo desactiva.</p>
          </div>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {(() => {
            const cv = Number(settings.credit_value_mxn) || 0;
            const mk = Number(settings.credit_markup) || 0;
            const pct = mk > 0 ? Math.round((mk - 1) * 100) : 0;
            return `100 créditos = ${formatMXN(100 * cv)} · Margen ${pct >= 0 ? '+' : ''}${pct}% sobre el costo real del modelo. El cobro es decimal (2 dec.) proporcional a los tokens.`;
          })()}
        </p>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input type="checkbox" data-testid="cfg-require-sub"
            checked={!!settings.require_subscription}
            onChange={(e) => setSettings((s) => ({ ...s, require_subscription: e.target.checked }))}
            className="accent-purple-600" />
          Exigir plan mensual activo para usar la IA (cuota de infraestructura; aplica a todos, incl. BYOK)
        </label>
        {settings.credit_packages.length > 0 && (
          <div className="space-y-2">
            <Label>Paquetes de créditos</Label>
            {/* Encabezados de columna */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs font-medium text-slate-500 dark:text-slate-400 px-1">
              <span>Nombre</span><span>Créditos</span><span>Precio (MXN)</span>
            </div>
            {settings.credit_packages.map((p, i) => (
              <div key={p.id || i} className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input value={p.name || ''} placeholder="Nombre" onChange={(e) => setPkg(i, 'name', e.target.value)} />
                <Input type="number" value={p.credits ?? ''} placeholder="Créditos" onChange={(e) => setPkg(i, 'credits', Number(e.target.value))} />
                <Input type="number" value={p.price_mxn ?? ''} placeholder="Precio MXN" onChange={(e) => setPkg(i, 'price_mxn', Number(e.target.value))} />
              </div>
            ))}
          </div>
        )}
        <Button onClick={saveConfig} disabled={savingCfg}
          className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700">
          {savingCfg ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Guardar configuración
        </Button>
      </CollapsibleCard>

      {/* Calculadora créditos -> tokens -> hojas (para decidir tarifas).
          Recibe settings del padre -> se refresca al guardar la config. */}
      <CreditCalculator settings={settings} />

      {/* Dev panel (collapsible) */}
      <div className="glass rounded-xl p-4">
        <button className="w-full flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400"
          onClick={() => setDevOpen((v) => !v)} data-testid="dash-dev-toggle">
          {devOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <FlaskConical className="w-4 h-4" /> Herramientas de desarrollo (simular datos)
        </button>
        {devOpen && (
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="space-y-1"><Label>Días</Label>
                <Input type="number" value={simDays} onChange={(e) => setSimDays(e.target.value)} /></div>
              <div className="space-y-1"><Label>Eventos/día</Label>
                <Input type="number" value={simEvents} onChange={(e) => setSimEvents(e.target.value)} /></div>
              <div className="space-y-1"><Label>Seed (opcional)</Label>
                <Input type="number" value={simSeed} onChange={(e) => setSimSeed(e.target.value)} /></div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={simulate} disabled={devBusy}>
                {devBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FlaskConical className="w-4 h-4 mr-1" />} Simular consumo
              </Button>
              <Button size="sm" variant="outline" onClick={clearSim} disabled={devBusy}
                className="text-red-600 hover:bg-red-100 dark:hover:bg-red-900/40">
                <Trash2 className="w-4 h-4 mr-1" /> Limpiar simulados
              </Button>
            </div>
          </div>
        )}
      </div>
      </>)}

      {/* Plan mensual (manual) modal */}
      <Dialog open={!!subUser} onOpenChange={(o) => !o && setSubUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Plan mensual de plataforma</DialogTitle>
            <DialogDescription>{subUser?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm">
              Estado:{' '}
              {subUser?.subscription?.active
                ? <span className="text-emerald-600 dark:text-emerald-400">Activo hasta {new Date(subUser.subscription.until).toLocaleDateString('es-MX')}</span>
                : <span className="text-slate-500 dark:text-slate-400">Sin plan</span>}
            </div>
            <div className="space-y-1">
              <Label>Meses a agregar (se suman al plan vigente)</Label>
              <Input type="number" min="1" max="60" value={subMonths}
                onChange={(e) => setSubMonths(e.target.value)} data-testid="sub-months" />
            </div>
          </div>
          <DialogFooter className="flex-wrap gap-2">
            {subUser?.subscription?.active && (
              <Button variant="outline" className="text-red-600 hover:text-red-700" disabled={subBusy}
                onClick={() => submitSub({ clear: true })}>Quitar plan</Button>
            )}
            <Button variant="outline" onClick={() => setSubUser(null)}>Cancelar</Button>
            <Button disabled={subBusy} onClick={() => submitSub({ months: Number(subMonths) || 1 })}>
              {subBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Activar / Extender
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit grant modal */}
      <Dialog open={!!creditUser} onOpenChange={(o) => !o && setCreditUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agregar créditos</DialogTitle>
            <DialogDescription>{creditUser?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Paquete</Label>
              <select value={creditPkg} onChange={(e) => { setCreditPkg(e.target.value); if (e.target.value) setCreditAmount(''); }}
                className={`${SELECT_CLS} w-full h-10`} data-testid="grant-package">
                <option value="" className={OPT_CLS}>— Monto libre —</option>
                {settings.credit_packages.map((p) => (
                  <option key={p.id} value={p.id} className={OPT_CLS}>{p.name} · {p.credits} cr · {formatMXN(p.price_mxn)}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Monto libre (créditos)</Label>
              <Input type="number" value={creditAmount} disabled={!!creditPkg}
                onChange={(e) => setCreditAmount(e.target.value)} data-testid="grant-amount" />
            </div>
            <div className="space-y-1">
              <Label>Nota</Label>
              <Input value={creditNote} onChange={(e) => setCreditNote(e.target.value)} placeholder="Motivo del ajuste" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditUser(null)}>Cancelar</Button>
            <Button onClick={submitCredit} disabled={grantBusy}>
              {grantBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Agregar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User detail modal */}
      <Dialog open={!!detailUser} onOpenChange={(o) => { if (!o) { setDetailUser(null); setDetailData(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalle de usuario</DialogTitle>
            <DialogDescription>{detailUser?.email}</DialogDescription>
          </DialogHeader>
          {detailBusy && <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>}
          {detailData && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                <div><span className="text-slate-500 dark:text-slate-400">Rol:</span> {detailData.role}</div>
                <div><span className="text-slate-500 dark:text-slate-400">Proveedor:</span> {detailData.auth_provider}</div>
                <div><span className="text-slate-500 dark:text-slate-400">Estado:</span> {detailData.active === false ? 'Inactivo' : 'Activo'}</div>
                <div><span className="text-slate-500 dark:text-slate-400">Saldo:</span> {formatInt(detailData.credits_balance)}</div>
                <div><span className="text-slate-500 dark:text-slate-400">Consumido:</span> {formatInt(detailData.totals?.total_credits_consumed)}</div>
                <div><span className="text-slate-500 dark:text-slate-400">Costo:</span> {formatUSD(detailData.totals?.total_cost_usd)}</div>
              </div>

              <div className="space-y-1">
                <span className="text-sm font-medium">Consumo reciente</span>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                        <th className="py-1 pr-2">Fecha</th><th className="py-1 px-2">Tipo</th><th className="py-1 px-2">Modelo</th>
                        <th className="py-1 px-2 text-right">Tokens</th><th className="py-1 px-2 text-right">Costo</th><th className="py-1 pl-2 text-right">Créditos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detailData.usage_events || []).map((e, i) => (
                        <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
                          <td className="py-1 pr-2">{String(e.created_at).slice(0, 19).replace('T', ' ')}</td>
                          <td className="py-1 px-2">{e.kind}</td>
                          <td className="py-1 px-2 font-mono">{e.model}</td>
                          <td className="py-1 px-2 text-right">{formatInt(e.tokens)}</td>
                          <td className="py-1 px-2 text-right">{formatUSD(e.cost_usd)}</td>
                          <td className="py-1 pl-2 text-right">{formatInt(e.credits_charged)}</td>
                        </tr>
                      ))}
                      {(detailData.usage_events || []).length === 0 && (
                        <tr><td colSpan={6} className="py-2 text-slate-500 dark:text-slate-400">Sin consumo.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="space-y-1">
                <span className="text-sm font-medium">Movimientos de créditos</span>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                        <th className="py-1 pr-2">Fecha</th><th className="py-1 px-2">Motivo</th>
                        <th className="py-1 px-2 text-right">Δ</th><th className="py-1 px-2 text-right">Saldo</th><th className="py-1 pl-2">Nota</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detailData.credit_ledger || []).map((l, i) => (
                        <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
                          <td className="py-1 pr-2">{String(l.created_at).slice(0, 19).replace('T', ' ')}</td>
                          <td className="py-1 px-2">{l.reason}</td>
                          <td className={`py-1 px-2 text-right ${l.delta < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{formatInt(l.delta)}</td>
                          <td className="py-1 px-2 text-right">{formatInt(l.balance_after)}</td>
                          <td className="py-1 pl-2 text-slate-500 dark:text-slate-400">{l.note || ''}</td>
                        </tr>
                      ))}
                      {(detailData.credit_ledger || []).length === 0 && (
                        <tr><td colSpan={5} className="py-2 text-slate-500 dark:text-slate-400">Sin movimientos.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailUser(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User edit modal */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar usuario</DialogTitle>
            <DialogDescription>{editUser?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Rol</Label>
              <select value={editRole} onChange={(e) => setEditRole(e.target.value)}
                className={`${SELECT_CLS} w-full h-10`} data-testid="edit-role">
                <option value="user" className={OPT_CLS}>user</option>
                <option value="admin" className={OPT_CLS}>admin</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={editActive}
                onChange={(e) => setEditActive(e.target.checked)} data-testid="edit-active" />
              Cuenta activa
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>Cancelar</Button>
            <Button onClick={submitEdit} disabled={editBusy}>
              {editBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
